import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getMessageStats, getNotificationStats } from '../api/messages';
import { handleApiError } from '../config/api';
import { useAuth } from '../hooks/useAuth';
import { useLatestRequestGuard } from '../hooks/useLatestRequestGuard';

/** Не дергать stats API на логине и без пользователя (избегаем 401 и лишней нагрузки). */
const shouldSkipStatsFetch = (pathname, uid) => {
  const isLoginRoute = typeof pathname === 'string' && pathname.startsWith('/login');
  const hasToken = Boolean(localStorage.getItem('token'));
  return isLoginRoute || !uid || !hasToken;
};

const NotificationContext = createContext(null);

/** Стабильный объект, если компонент вызван вне NotificationProvider (без падения UI). */
const NOTIFICATION_COUNTS_FALLBACK = {
  unreadMessages: 0,
  unreadNotifications: 0,
  loading: false,
  updateCounts: async () => {},
  decrementMessages: () => {},
  decrementNotifications: () => {},
  incrementMessages: () => {},
  incrementNotifications: () => {}
};

// Константы для кеширования
const CACHE_KEY_MESSAGES = 'notification_stats_messages';
const CACHE_KEY_NOTIFICATIONS = 'notification_stats_notifications';
const CACHE_VERSION = 'v1';
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

// Утилиты для работы с кешем
const getCachedStats = (key) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp, version } = JSON.parse(cached);
    
    // Проверяем версию и время жизни кеша
    if (version !== CACHE_VERSION) {
      sessionStorage.removeItem(key);
      return null;
    }
    
    const age = Date.now() - timestamp;
    if (age > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

const setCachedStats = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    }));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

/** Хук для доступа к счётчикам уведомлений и сообщений (бейдж в шапке). */
export const useNotificationCounts = () => {
  const context = useContext(NotificationContext);
  if (context == null) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'useNotificationCounts: нет NotificationProvider — счётчики отключены (fallback).'
      );
    }
    return NOTIFICATION_COUNTS_FALLBACK;
  }
  return context;
};

/** Обратная совместимость: алиас для useNotificationCounts. */
export const useNotifications = useNotificationCounts;

export const NotificationProvider = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const lastVisibilityFetchRef = useRef(0);
  const lastStatsUserUidRef = useRef(undefined);
  const updateCountsInflightRef = useRef(null);
  const { begin: beginCountsUpdate, isCurrent: isCountsUpdateCurrent } = useLatestRequestGuard();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      beginCountsUpdate();
    };
  }, [beginCountsUpdate]);

  const updateCounts = useCallback(async (forceRefresh = false) => {
    const uid = userRef.current?.profile_uid;
    if (shouldSkipStatsFetch(pathname, uid)) {
      if (mountedRef.current) {
        setUnreadMessages(0);
        setUnreadNotifications(0);
      }
      setLoading(false);
      return;
    }

    if (updateCountsInflightRef.current) {
      return updateCountsInflightRef.current;
    }

    const task = (async () => {
      const reqId = beginCountsUpdate();
      try {
        // Сначала проверяем кеш (если не force refresh)
        if (!forceRefresh) {
          const cachedMessages = getCachedStats(CACHE_KEY_MESSAGES);
          const cachedNotifications = getCachedStats(CACHE_KEY_NOTIFICATIONS);

          if (cachedMessages !== null && cachedNotifications !== null) {
            if (mountedRef.current && isCountsUpdateCurrent(reqId)) {
              setUnreadMessages(Math.max(0, Number(cachedMessages.unread_messages) || 0));
              setUnreadNotifications(Math.max(0, Number(cachedNotifications.unread_count) || 0));
            }
            if (isCountsUpdateCurrent(reqId)) {
              setLoading(false);
            }
            const [msgResult, notifResult] = await Promise.allSettled([
              getMessageStats(),
              getNotificationStats()
            ]);
            if (!isCountsUpdateCurrent(reqId)) return;
            if (msgResult.status === 'fulfilled' && msgResult.value != null) {
              const payload = msgResult.value.data ?? msgResult.value;
              const n = Math.max(0, Number(payload.unread_messages) || 0);
              setUnreadMessages(n);
              setCachedStats(CACHE_KEY_MESSAGES, payload);
            }
            if (notifResult.status === 'fulfilled' && notifResult.value != null) {
              const payload = notifResult.value.data ?? notifResult.value;
              const n = Math.max(0, Number(payload.unread_count) || 0);
              setUnreadNotifications(n);
              setCachedStats(CACHE_KEY_NOTIFICATIONS, payload);
            }
            return;
          }
        }

        const [msgResult, notifResult] = await Promise.allSettled([
          getMessageStats(),
          getNotificationStats()
        ]);

        if (!isCountsUpdateCurrent(reqId)) return;

        if (msgResult.status === 'fulfilled' && msgResult.value != null) {
          const payload = msgResult.value.data ?? msgResult.value;
          const raw = payload.unread_messages;
          const newUnreadMessages = Math.max(0, Number(raw) || 0);
          setUnreadMessages(newUnreadMessages);
          setCachedStats(CACHE_KEY_MESSAGES, payload);
        }
        if (notifResult.status === 'fulfilled' && notifResult.value != null) {
          const payload = notifResult.value.data ?? notifResult.value;
          const raw = payload.unread_count;
          const newUnreadNotifications = Math.max(0, Number(raw) || 0);
          setUnreadNotifications(newUnreadNotifications);
          setCachedStats(CACHE_KEY_NOTIFICATIONS, payload);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('NotificationContext: ошибка обновления счётчиков:', handleApiError(err));
        }
      } finally {
        if (isCountsUpdateCurrent(reqId)) {
          setLoading(false);
        }
      }
    })();

    updateCountsInflightRef.current = task;
    void task.finally(() => {
      if (updateCountsInflightRef.current === task) {
        updateCountsInflightRef.current = null;
      }
    });

    return task;
  }, [pathname, beginCountsUpdate, isCountsUpdateCurrent]);

  const decrementMessages = useCallback((count = 1) => {
    try {
      sessionStorage.removeItem(CACHE_KEY_MESSAGES);
    } catch {
      /* ignore */
    }
    setUnreadMessages(prev => Math.max(0, prev - count));
  }, []);

  const decrementNotifications = useCallback((count = 1) => {
    try {
      sessionStorage.removeItem(CACHE_KEY_NOTIFICATIONS);
    } catch {
      /* ignore */
    }
    setUnreadNotifications(prev => Math.max(0, prev - count));
  }, []);

  const incrementMessages = useCallback((count = 1) => {
    try {
      sessionStorage.removeItem(CACHE_KEY_MESSAGES);
    } catch {
      /* ignore */
    }
    setUnreadMessages(prev => prev + count);
  }, []);

  const incrementNotifications = useCallback((count = 1) => {
    try {
      sessionStorage.removeItem(CACHE_KEY_NOTIFICATIONS);
    } catch {
      /* ignore */
    }
    setUnreadNotifications(prev => prev + count);
  }, []);

  useEffect(() => {
    const uid = user?.profile_uid ?? null;
    if (lastStatsUserUidRef.current !== uid) {
      lastStatsUserUidRef.current = uid;
      try {
        sessionStorage.removeItem(CACHE_KEY_MESSAGES);
        sessionStorage.removeItem(CACHE_KEY_NOTIFICATIONS);
      } catch {
        /* ignore */
      }
    }
    if (!user?.profile_uid) {
      setUnreadMessages(0);
      setUnreadNotifications(0);
      setLoading(false);
    } else {
      setLoading(true);
      void updateCounts(true);
    }
  }, [user?.profile_uid, updateCounts]);

  useEffect(() => {
    if (shouldSkipStatsFetch(pathname, user?.profile_uid)) {
      return undefined;
    }

    const interval = setInterval(() => {
      updateCounts(true);
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [updateCounts, pathname, user?.profile_uid]);

  useEffect(() => {
    if (shouldSkipStatsFetch(pathname, user?.profile_uid)) {
      return undefined;
    }
    const refreshCountsFromEvents = () => {
      updateCounts(true);
    };
    window.addEventListener('notification_received', refreshCountsFromEvents);
    window.addEventListener('notification_counts_refresh', refreshCountsFromEvents);
    return () => {
      window.removeEventListener('notification_received', refreshCountsFromEvents);
      window.removeEventListener('notification_counts_refresh', refreshCountsFromEvents);
    };
  }, [updateCounts, pathname, user?.profile_uid]);

  useEffect(() => {
    if (shouldSkipStatsFetch(pathname, user?.profile_uid)) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityFetchRef.current < 20000) return;
      lastVisibilityFetchRef.current = now;
      updateCounts(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.profile_uid, pathname, updateCounts]);

  const value = useMemo(() => ({
    unreadMessages,
    unreadNotifications,
    loading,
    updateCounts,
    decrementMessages,
    decrementNotifications,
    incrementMessages,
    incrementNotifications
  }), [unreadMessages, unreadNotifications, loading, updateCounts, decrementMessages, decrementNotifications, incrementMessages, incrementNotifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext; 